import React, { useEffect } from "react";

type ConfirmVariant = "primary" | "danger" | "warning";

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  isSubmitting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

type BackdropSize = "md" | "lg" | "2xl";

const SIZE_CLASS: Record<BackdropSize, string> = {
  md: "max-w-md",
  lg: "max-w-lg",
  "2xl": "max-w-2xl",
};

export const Backdrop: React.FC<{
  onClose?: () => void;
  size?: BackdropSize;
  children: React.ReactNode;
}> = ({ onClose, size = "md", children }) => {
  useEffect(() => {
    if (!onClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`w-full ${SIZE_CLASS[size]} p-6 mx-4 bg-white rounded-lg shadow-xl`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = "primary",
  isSubmitting = false,
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;

  const confirmClasses =
    variant === "danger"
      ? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
      : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500";

  return (
    <Backdrop onClose={isSubmitting ? undefined : onCancel}>
      <h3 className="mb-3 text-lg font-semibold text-gray-900">{title}</h3>
      <div className="mb-6 text-sm text-gray-700 whitespace-pre-line">
        {message}
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"
        >
          {cancelLabel ?? "Cancel"}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isSubmitting}
          className={`px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2 disabled:opacity-60 ${confirmClasses}`}
        >
          {isSubmitting ? "Working..." : (confirmLabel ?? "Confirm")}
        </button>
      </div>
    </Backdrop>
  );
};

type InfoVariant = "success" | "info" | "warning";

interface InfoModalProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  buttonLabel?: string;
  variant?: InfoVariant;
  onClose: () => void;
}

const VARIANT_ICON: Record<InfoVariant, { bg: string; fg: string; path: string }> = {
  success: {
    bg: "bg-green-100",
    fg: "text-green-600",
    path: "M5 13l4 4L19 7",
  },
  info: {
    bg: "bg-blue-100",
    fg: "text-blue-600",
    path: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  warning: {
    bg: "bg-yellow-100",
    fg: "text-yellow-600",
    path: "M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z",
  },
};

export interface ConflictFieldRow {
  field: string;
  label: string;
  mine: unknown;
  server: unknown;
}

interface ConflictModalProps {
  open: boolean;
  rowKey: string | number;
  rows: ConflictFieldRow[];
  isSubmitting?: boolean;
  onAcceptServer: () => void;
  onOverwrite: () => void;
  onCancel: () => void;
}

const formatConflictValue = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

export const ConflictModal: React.FC<ConflictModalProps> = ({
  open,
  rowKey,
  rows,
  isSubmitting = false,
  onAcceptServer,
  onOverwrite,
  onCancel,
}) => {
  if (!open) return null;

  return (
    <Backdrop onClose={isSubmitting ? undefined : onCancel} size="2xl">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-yellow-100 shrink-0">
          <svg
            className="w-5 h-5 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Conflict on row #{rowKey}
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Someone else updated this row before you saved. Pick one of the
            actions below — your unsaved values are preserved until you choose.
          </p>
        </div>
      </div>

      <div className="mb-6 overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Field</th>
              <th className="px-3 py-2 text-left font-medium">Your value</th>
              <th className="px-3 py-2 text-left font-medium">Server value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.field}>
                <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                  {r.label}
                </td>
                <td className="px-3 py-2 text-blue-700 break-all">
                  {formatConflictValue(r.mine)}
                </td>
                <td className="px-3 py-2 text-gray-700 break-all">
                  {formatConflictValue(r.server)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"
        >
          Keep editing
        </button>
        <button
          type="button"
          onClick={onAcceptServer}
          disabled={isSubmitting}
          className="px-4 py-2 text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-60"
        >
          Use server value
        </button>
        <button
          type="button"
          onClick={onOverwrite}
          disabled={isSubmitting}
          className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-60"
        >
          {isSubmitting ? "Overwriting..." : "Overwrite with mine"}
        </button>
      </div>
    </Backdrop>
  );
};

export interface SavePreviewChange {
  field: string;
  label: string;
  before: unknown;
  after: unknown;
}

export interface SavePreviewRowChange {
  pk: number;
  changes: SavePreviewChange[];
}

interface SavePreviewModalProps {
  open: boolean;
  rowChanges: SavePreviewRowChange[];
  isSubmitting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const formatPreviewValue = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "(empty)";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

export const SavePreviewModal: React.FC<SavePreviewModalProps> = ({
  open,
  rowChanges,
  isSubmitting = false,
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;
  const totalCells = rowChanges.reduce((s, r) => s + r.changes.length, 0);

  return (
    <Backdrop onClose={isSubmitting ? undefined : onCancel} size="2xl">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 shrink-0">
          <svg
            className="w-5 h-5 text-amber-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z"
            />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Review changes</h3>
          <p className="mt-1 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{totalCells}</span> cell
            {totalCells === 1 ? "" : "s"} changed across{" "}
            <span className="font-semibold text-gray-900">
              {rowChanges.length}
            </span>{" "}
            row{rowChanges.length === 1 ? "" : "s"}. Verify each before/after
            below before writing to the database.
          </p>
        </div>
      </div>

      <div className="mb-6 max-h-[55vh] overflow-y-auto pr-1 space-y-4">
        {rowChanges.map((row) => (
          <div
            key={row.pk}
            className="overflow-hidden border border-gray-200 rounded-lg"
          >
            <div className="px-3 py-2 text-xs font-semibold tracking-wide text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
              Row #{row.pk}
              <span className="ml-2 text-[11px] font-normal text-gray-500 normal-case">
                {row.changes.length} field
                {row.changes.length === 1 ? "" : "s"}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-gray-500 bg-white">
                <tr>
                  <th className="px-3 py-2 font-medium text-left border-b border-gray-100 w-1/4">
                    Field
                  </th>
                  <th className="px-3 py-2 font-medium text-left border-b border-gray-100">
                    Before
                  </th>
                  <th className="px-3 py-2 font-medium text-left border-b border-gray-100">
                    After
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {row.changes.map((c) => (
                  <tr key={c.field}>
                    <td className="px-3 py-2 font-medium text-gray-900 align-top whitespace-nowrap">
                      {c.label}
                    </td>
                    <td className="px-3 py-2 text-gray-500 break-all line-through align-top">
                      {formatPreviewValue(c.before)}
                    </td>
                    <td className="px-3 py-2 font-medium align-top break-all text-amber-900 bg-amber-50">
                      {formatPreviewValue(c.after)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
        >
          {isSubmitting ? "Saving..." : "Confirm save"}
        </button>
      </div>
    </Backdrop>
  );
};

export const InfoModal: React.FC<InfoModalProps> = ({
  open,
  title,
  message,
  buttonLabel,
  variant = "success",
  onClose,
}) => {
  if (!open) return null;
  const icon = VARIANT_ICON[variant];

  return (
    <Backdrop onClose={onClose}>
      <div className="flex flex-col items-center">
        <div
          className={`flex items-center justify-center w-12 h-12 mb-4 rounded-full ${icon.bg}`}
        >
          <svg
            className={`w-6 h-6 ${icon.fg}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d={icon.path}
            />
          </svg>
        </div>
        <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
        <div className="mb-6 text-sm text-center text-gray-700 whitespace-pre-line">
          {message}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {buttonLabel ?? "OK"}
        </button>
      </div>
    </Backdrop>
  );
};
